/**
 * Phase 20 — Admin Water Test Sticker / Field Label page.
 *
 * Route: /admin/partners/field-labels
 *
 * PINNED USER RULE (Feb 2026):
 *   Phase 20 NEVER sends emails / SMS / social posts, NEVER triggers
 *   AusPost / Reply Paid / Return Paid label generation, NEVER calls a
 *   label printer, NEVER publishes a public page. This page is
 *   preview / download only.
 *
 * Surfaces:
 *   1. Safety banner — "preview only · no emails / SMS / AusPost / printers".
 *   2. Mode toggle — partner-branded label OR SCWT-only fallback.
 *   3. Partner selector — only active PWS partners (partner-branded mode).
 *   4. Template previews (3): sticker_small, dl_card, a4_sheet.
 *   5. Sticker SVG preview block with PNG/SVG/HTML download buttons.
 *   6. Compliance block — wording rules + AusPost stub disclosure.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";
import {
  ArrowLeft, RefreshCw, AlertTriangle, Loader2, ShieldAlert,
  Sticker, ExternalLink, FileText, Layers, Printer,
} from "lucide-react";
import { toast } from "sonner";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { PortalShell } from "@/components/PortalShell";


// ── Mode toggle ─────────────────────────────────────────────────────────
function ModeToggle({ mode, onChange }) {
  return (
    <section
      data-testid="ph20-mode-toggle"
      className="rounded-2xl bg-white border border-slate-200 p-4 mt-4"
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] font-bold text-slate-700 mb-3">
        <Layers className="w-3.5 h-3.5 text-cyan-700" /> Label mode
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          data-testid="ph20-mode-partner"
          onClick={() => onChange("partner")}
          className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${
            mode === "partner"
              ? "bg-cyan-700 text-white border-cyan-700"
              : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
          }`}
        >
          Partner-branded
        </button>
        <button
          type="button"
          data-testid="ph20-mode-scwt"
          onClick={() => onChange("scwt")}
          className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${
            mode === "scwt"
              ? "bg-slate-800 text-white border-slate-800"
              : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
          }`}
        >
          SCWT-only (no partner)
        </button>
      </div>
      <p className="mt-2 text-[10px] text-slate-400 italic">
        SCWT-only labels are used for direct bookings, lab drop-off kits
        and any kit not yet attributed to a PWS partner.
      </p>
    </section>
  );
}


// ── Partner selector ────────────────────────────────────────────────────
function PartnerSelector({ partners, partnerId, onChange }) {
  return (
    <section
      data-testid="ph20-partner-selector"
      className="rounded-2xl bg-white border border-slate-200 p-4 mt-4"
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] font-bold text-slate-700 mb-2">
        <Sticker className="w-3.5 h-3.5 text-cyan-700" /> Select partner
      </div>
      <select
        data-testid="ph20-partner-select"
        value={partnerId || ""}
        onChange={(e) => onChange(e.target.value || null)}
        className="text-sm w-full border border-slate-200 rounded-md px-2 py-2 bg-white"
      >
        <option value="">— Select an active PWS partner —</option>
        {partners.map((p) => (
          <option key={p.id} value={p.id}>
            {p.business_name} · {p.franchise_region || "—"} ({p.partner_slug})
          </option>
        ))}
      </select>
      <p className="mt-2 text-[10px] text-slate-400 italic">
        Only active partners are listed. Draft / inactive / suspended /
        missing-slug partners are excluded.
      </p>
    </section>
  );
}


// ── Preview header — context + booking URL + QR ─────────────────────────
function PreviewHeader({ preview }) {
  if (!preview) return null;
  const ctx = preview.context;
  return (
    <section
      data-testid="ph20-preview-header"
      className="rounded-2xl bg-white border border-slate-200 p-5 mt-4"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">
            Trading name
          </div>
          <div data-testid="ph20-trading-name"
               className="font-semibold text-slate-800">
            {ctx.trading_name}
          </div>
          {ctx.region_strap && (
            <div data-testid="ph20-region-strap" className="text-xs text-slate-600 mt-1">
              {ctx.region_strap}
            </div>
          )}
          <div data-testid="ph20-scwt-strap" className="text-xs text-slate-500 mt-1">
            {ctx.scwt_strap}
          </div>
          <div className="mt-2 text-[10px] uppercase tracking-wider text-slate-500">
            Booking URL
          </div>
          <a
            href={ctx.booking_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="ph20-booking-url"
            className="text-blue-700 hover:underline break-all font-mono text-xs"
          >
            {ctx.booking_url}
          </a>
        </div>
        {ctx.qr_png_data_url && (
          <div
            data-testid="ph20-qr-thumbnail"
            className="rounded-md border border-sky-200 bg-sky-50 p-2"
          >
            <img
              src={ctx.qr_png_data_url}
              alt="Field label QR code"
              width={96}
              height={96}
              className="rounded-sm bg-white p-1"
            />
            <div className="mt-1 text-[10px] text-center text-sky-700 font-semibold">
              QR v{ctx.qr_version}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}


// ── Template card ───────────────────────────────────────────────────────
function TemplateCard({ slug, tpl, mode, partnerId, sticker_svg }) {
  // Both partner & scwt modes have a /html endpoint and the small
  // sticker has a /svg endpoint.
  const htmlHref = mode === "scwt"
    ? `${API_BASE}/admin/pws-field-labels/scwt/${slug}.html`
    : `${API_BASE}/admin/pws-field-labels/${encodeURIComponent(partnerId)}/${slug}.html`;
  const svgHref = mode === "scwt"
    ? `${API_BASE}/admin/pws-field-labels/scwt/sticker_small.svg`
    : `${API_BASE}/admin/pws-field-labels/${encodeURIComponent(partnerId)}/sticker_small.svg`;
  return (
    <section
      data-testid={`ph20-template-${slug}`}
      className="rounded-2xl bg-white border border-slate-200 p-5 mt-4"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-700 flex items-center gap-1.5">
          <Printer className="w-3.5 h-3.5 text-slate-700" /> {tpl.label}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <a
            href={htmlHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`ph20-open-html-${slug}`}
            className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full border border-slate-200 hover:bg-slate-50"
          >
            <ExternalLink className="w-3 h-3" /> Open HTML
          </a>
          {slug === "sticker_small" && (
            <a
              href={svgHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`ph20-download-svg-${slug}`}
              className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full border border-sky-200 text-sky-800 hover:bg-sky-50"
            >
              <FileText className="w-3 h-3" /> Download SVG
            </a>
          )}
        </div>
      </div>
      {/* Inline HTML preview via srcDoc — sandboxed so no scripts can run. */}
      <div
        data-testid={`ph20-preview-frame-${slug}`}
        className="rounded-lg border border-slate-200 bg-slate-50 overflow-hidden"
      >
        <iframe
          title={`Preview ${tpl.label}`}
          srcDoc={tpl.html}
          sandbox=""
          style={{
            width: "100%",
            height: slug === "a4_sheet" ? "640px" : "180px",
            border: 0,
            backgroundColor: "white",
          }}
        />
      </div>
      {slug === "sticker_small" && sticker_svg && (
        <div
          data-testid="ph20-sticker-svg-preview"
          className="mt-3 rounded-lg border border-sky-200 bg-sky-50 p-3 flex items-center justify-center"
          // Phase 2L-security-M3 (Feb-2026): sticker SVG is admin-only
          // and server-generated, but we still sanitise via DOMPurify's
          // SVG profile so any injected <script> / event handler is
          // stripped before it hits the admin DOM.
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(sticker_svg, {
              USE_PROFILES: { svg: true, svgFilters: true },
            }),
          }}
        />
      )}
      <p className="mt-2 text-[10px] text-slate-400 italic">
        Print via the browser&apos;s print dialog from the Open HTML view.
        No external resources, no scripts, no trackers.
      </p>
    </section>
  );
}


// ── Compliance / return-label disclosure ────────────────────────────────
function ComplianceCard({ preview }) {
  if (!preview) return null;
  return (
    <section
      data-testid="ph20-compliance-card"
      className="rounded-2xl bg-slate-50 border border-slate-200 p-5 mt-4"
    >
      <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-700 mb-2">
        Wording &amp; integration boundary
      </div>
      <p className="text-xs text-slate-700">{preview.compliance_note}</p>
      <div className="mt-3 text-[10px] text-slate-500">
        <strong>Australia Post integration:</strong>{" "}
        <span data-testid="ph20-return-label-type">
          {preview.return_label.return_label_type}
        </span>{" "}·{" "}
        <span data-testid="ph20-return-label-status">
          {preview.return_label.return_label_status}
        </span>
      </div>
      <div className="text-[10px] text-slate-500 mt-1 italic">
        {preview.return_label.note}
      </div>
      <div
        data-testid="ph20-dispatch-disabled-reason"
        className="mt-3 text-[10px] text-slate-500 italic"
      >
        {preview.dispatch_disabled_reason}
      </div>
    </section>
  );
}


// ── Page ────────────────────────────────────────────────────────────────
export default function AdminPWSFieldLabels() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [partners, setPartners] = useState([]);
  const [mode, setMode] = useState("partner");      // "partner" | "scwt"
  const [partnerId, setPartnerId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Load active partner list on mount.
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await api.get("/admin/pws-field-labels/partners");
        if (!alive) return;
        setPartners(data.partners || []);
      } catch (e) {
        if (alive) setError(formatApiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Fetch preview when mode/partner changes.
  useEffect(() => {
    setPreview(null);
    if (mode === "partner" && !partnerId) return;
    let alive = true;
    (async () => {
      setPreviewLoading(true);
      try {
        const url = mode === "scwt"
          ? "/admin/pws-field-labels/scwt"
          : `/admin/pws-field-labels/${encodeURIComponent(partnerId)}`;
        const { data } = await api.get(url);
        if (!alive) return;
        setPreview(data);
      } catch (e) {
        if (alive) {
          toast.error(formatApiError(e) || "Preview load failed.");
          setPreview(null);
        }
      } finally {
        if (alive) setPreviewLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [mode, partnerId]);

  const onModeChange = useCallback((m) => {
    setMode(m);
    if (m === "scwt") setPartnerId(null);
  }, []);

  const reset = useCallback(() => {
    setMode("partner");
    setPartnerId(null);
    setPreview(null);
  }, []);

  const selectedPartnerLabel = useMemo(() => {
    if (mode === "scwt") return "SCWT-only fallback label";
    const p = partners.find((x) => x.id === partnerId);
    return p ? `${p.business_name} (${p.partner_slug})` : null;
  }, [mode, partners, partnerId]);

  const tplBlock = preview?.templates || {};

  return (
    <PortalShell>
      <div
        data-testid="admin-pws-field-labels-page"
        className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
      >
        <Link
          to="/admin/partners/network"
          data-testid="ph20-back-to-network"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 hover:text-blue-800"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to PWS Network
        </Link>

        <div className="mt-3 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-700">
              Aware Partner Network · Phase 20
            </div>
            <h1 className="font-display mt-2 text-3xl font-bold text-slate-900">
              Water Test Sticker &amp; Field Label
            </h1>
            <p className="text-slate-500 mt-1 text-sm max-w-3xl">
              Generate printable / downloadable field labels for sample
              kits, return satchels, customer instruction cards and tank
              inspection forms. Choose a partner-branded label (with the
              partner&apos;s booking QR) or an SCWT-only fallback for direct
              bookings.{" "}
              <strong className="text-slate-700">
                Phase 20 never sends emails, SMS, social posts or postage
                labels, and never calls Australia Post or a label printer.
              </strong>
            </p>
            {selectedPartnerLabel && (
              <p
                data-testid="ph20-selected-label"
                className="mt-1 text-xs text-cyan-700 font-semibold"
              >
                Showing: {selectedPartnerLabel}
              </p>
            )}
          </div>
          <button
            onClick={reset}
            data-testid="ph20-reset-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Reset
          </button>
        </div>

        {/* Safety banner */}
        <div
          data-testid="ph20-safety-banner"
          className="mt-5 rounded-xl bg-cyan-50 border border-cyan-200 p-4 flex items-start gap-3 text-sm text-cyan-900"
        >
          <ShieldAlert className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div>
            <strong>Preview &amp; download only.</strong> No emails, SMS
            or social posts are sent. No postage labels, Reply Paid or
            Return Paid labels are generated. No AusPost calls. No label
            printers are triggered. No public pages are published. All
            copy uses approved calm wording, avoids medical / scare /
            NATA claims, and never defaults to Soda Ash or Reverse
            Osmosis.
          </div>
        </div>

        {loading ? (
          <div
            data-testid="ph20-loading"
            className="mt-8 rounded-2xl bg-white border border-slate-200 p-10 flex items-center gap-2 text-slate-400 justify-center"
          >
            <Loader2 className="w-5 h-5 animate-spin" /> Loading partners…
          </div>
        ) : error ? (
          <div
            data-testid="ph20-error"
            className="mt-8 rounded-xl bg-rose-50 border border-rose-200 p-5 text-sm text-rose-800"
          >
            <div className="font-semibold mb-1 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Failed to load
            </div>
            <div className="text-xs">{error}</div>
          </div>
        ) : (
          <>
            <ModeToggle mode={mode} onChange={onModeChange} />
            {mode === "partner" && (
              <PartnerSelector
                partners={partners}
                partnerId={partnerId}
                onChange={setPartnerId}
              />
            )}
            {previewLoading && (
              <div
                data-testid="ph20-preview-loading"
                className="mt-8 rounded-2xl bg-white border border-slate-200 p-10 flex items-center gap-2 text-slate-400 justify-center"
              >
                <Loader2 className="w-5 h-5 animate-spin" /> Loading preview…
              </div>
            )}
            {preview && !previewLoading && (
              <div data-testid="ph20-preview-body">
                <PreviewHeader preview={preview} />
                <TemplateCard
                  slug="sticker_small"
                  tpl={tplBlock.sticker_small}
                  mode={mode}
                  partnerId={partnerId}
                  sticker_svg={tplBlock.sticker_small?.svg}
                />
                <TemplateCard
                  slug="dl_card"
                  tpl={tplBlock.dl_card}
                  mode={mode}
                  partnerId={partnerId}
                />
                <TemplateCard
                  slug="a4_sheet"
                  tpl={tplBlock.a4_sheet}
                  mode={mode}
                  partnerId={partnerId}
                />
                <ComplianceCard preview={preview} />
              </div>
            )}
          </>
        )}
      </div>
    </PortalShell>
  );
}
